// Imports
import Vue from 'vue'
import VueRouter from 'vue-router'
import Meta from 'vue-meta'
import posthog from 'posthog-js'

import store from '../store'
import { clearUniqueCreatives } from '../db/UniqueCreativesDB'
import { clearUniqueHooks } from '../db/UniqueHooksDB'

import firebase from '@/api/config/FirebaseConfig'

import { SUBSCRIPTION_STATES } from '@/utils/stripe/productMeta'
import { isAppRedirect } from '@/utils/auth/isAppRedirect'

import EventBus from '@/plugins/BaseNotifications/EventBus'
import { isDemoLensId, isDemoLensRoute } from '@/utils/lens/demo/lensDemoConstants'
import DEMO_METRICS_REGISTRY from '@/utils/lens/demo/lensDemoMetricsRegistry'
import { FEATURE_FLAGS } from '@/utils/featureFlags'

// FOR DEVELOPMENT
import ComponentView from '@/views/dev/ComponentView'
import { _ } from 'core-js'

// Dynamic imports
const AccountView = () => import('@/views/account/AccountView')
const AdsPublicView = () => import('../views/ads/AdsPublicView')
const AdsPublicViewDiscover = () => import('../views/ads/AdsPublicViewDiscover')
const AdsShareLensView = () => import('../views/ads/AdsShareLensView.vue')
const AuthCallbackView = () => import('../views/auth/AuthCallbackView')
const BoardsDetailsView = () => import('@/views/boards/BoardsDetailsView')
const BoardsPublicView = () => import('../views/boards/BoardsPublicView')
const BoardsView = () => import('@/views/boards/BoardsView')
const BountiesView = () => import('@/views/bounties/BountiesView')
const BrandView = () => import('@/views/brands/BrandView')
const BriefPublicViewV2 = () => import('../views/briefs/BriefPublicViewV2.vue')
const BriefsView = () => import('../views/briefs/BriefsView.vue')
const ChromeExtensionAuthView = () => import('../views/auth/ChromeExtensionAuthView')
const DashboardView = () => import('@/views/dashboard/DashboardView')
const DataApiView = () => import('@/views/api/DataApiView')
const DiscoveryAICategoryView = () => import('../views/discover/DiscoveryAICategoryView')
const DiscoveryBrandView = () => import('@/views/brands/DiscoveryBrandView')
const DiscoveryExpertPublicView = () => import('@/views/discover/DiscoveryExpertPublicView.vue')
const DiscoveryExpertView = () => import('@/views/discover/DiscoveryExpertView.vue')
const DiscoveryView = () => import('../views/discover/DiscoveryView')
const EditBriefView = () => import('../views/briefs/EditBriefView')
const ExpertRedirectView = () => import('../views/discover/ExpertRedirectView.vue')
const ForgotPasswordView = () => import('@/views/auth/ForgotPasswordView')
const ResetPasswordView = () => import('@/views/auth/ResetPasswordView')
const LensView = () => import('@/views/lens/LensView')
const LensReportView = () => import('@/views/lens/LensReportView')
const LensReportPublicView = () => import('@/views/lens/LensReportPublicView')
const LensDashboardView = () => import('@/views/lens/LensDashboardView')
const LibraryView = () => import('@/views/library/LibraryView')
const LoginEmailSentView = () => import('../views/auth/LoginEmailSentView')
const LoginView = () => import('@/views/auth/LoginView')
const McpOauthAuthorizeView = () => import('@/views/auth/McpOauthAuthorizeView')
const PaymentViewV2 = () => import('@/views/account/PaymentViewV2')
const PerksView = () => import('@/views/account/PerksView')
const SetupView = () => import('@/views/setup/SetupView')
const SignUpView = () => import('@/views/auth/SignUpView')
const SidebarUITesting = () => import('@/views/testing/SidebarUITesting.vue')
const VerifyEmailView = () => import('@/views/auth/VerifyEmailView')
const CompleteVerificationView = () => import('@/views/auth/CompleteVerificationView')
const SpyderBrandsView = () => import('../views/spyder/SpyderBrandsView.vue')
const SpyderFeedView = () => import('../views/spyder/SpyderFeedView.vue')
const SpyderView = () => import('@/views/spyder/SpyderView')
const ThankYouRedirectView = () => import('@/views/account/ThankYouRedirectView')
const TokenLoginView = () => import('../views/auth/TokenLoginView')
const SuccessPurchaseView = () => import('../views/account/SuccessPurchaseView.vue')
const UserOnboardView = () => import('../views/auth/UserOnboardView.vue')
const FacebookAuthSuccessView = () => import('../views/lens/FacebookAuthSuccessView.vue')
const FacebookDeleteDataView = () => import('../views/lens/FacebookDeleteDataView.vue')
const TeamInviteView = () => import('@/views/auth/TeamInviteView.vue')
// Install Router
Vue.use(VueRouter)

// Install Meta
Vue.use(Meta)

// Main Routes
const routes = [
  {
    path: '/components',
    name: 'ComponentView',
    component: ComponentView
  },
  {
    path: '/sidebar-ui-testing',
    name: 'SidebarUITesting',
    component: SidebarUITesting,
    meta: { requiresAuth: true }
  },
  {
    path: '/bounties',
    name: 'BountiesView',
    component: BountiesView,
    meta: { requiresAuth: true, theme: 'black', product: 'bounties' }
  },
  {
    path: '/api-overview',
    name: 'ApiView',
    component: DataApiView,
    meta: { requiresAuth: true, theme: 'black', product: 'api' }
  },
  {
    path: '/api-logs',
    name: 'ApiLogsView',
    component: DataApiView,
    meta: { requiresAuth: true, theme: 'black', product: 'api' }
  },
  {
    path: '/api-credits',
    name: 'ApiCreditsView',
    component: DataApiView,
    meta: { requiresAuth: true, theme: 'black', product: 'api' }
  },
  {
    path: '/lens',
    name: 'LensView',
    component: LensView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens' }
  },
  {
    path: '/connect-facebook-lens/delete-data',
    name: 'FacebookDeleteDataView',
    component: FacebookDeleteDataView,
    meta: { requiresAuth: false, theme: 'black', hideSidebar: true }
  },
  {
    path: '/connect-facebook-lens/success',
    name: 'FacebookAuthSuccessView',
    component: FacebookAuthSuccessView,
    meta: { requiresAuth: true, theme: 'blue', product: 'lens', hideSidebar: true }
  },
  {
    path: '/lens/:lensId',
    name: 'LensDashboardView',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'dashboard' }
    // children: [
    //   {
    //     path: '',
    //     name: 'LensDashboard',
    //     meta: { requiresAuth: true, tab: 'dashboard', theme: 'black', product: 'lens'  }
    //   },
    //   {
    //     path: 'creative-tests',
    //     name: 'LensCreativeTests',
    //     meta: { requiresAuth: true, tab: 'creative-tests', theme: 'black', product: 'lens' }
    //   },
    //   {
    //     path: 'reports',
    //     name: 'LensReports',
    //     meta: { requiresAuth: true, tab: 'reports', theme: 'black', product: 'lens' }
    //   },
    //   {
    //     path: 'inspiration',
    //     name: 'LensInspiration',
    //     meta: { requiresAuth: true, tab: 'inspiration', theme: 'black', product: 'lens' }
    //   }
    // ]
  },
  {
    path: '/lens/:lensId/reports',
    name: 'LensReports',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'reports' }
  },
  {
    path: '/lens/:lensId/creative-tests',
    name: 'LensCreativeTests',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'creative-tests' }
  },
  {
    path: '/lens/:lensId/inspiration',
    name: 'LensInspiration',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'inspiration' }
  },
  {
    path: '/lens/:lensId/comments',
    name: 'LensComments',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'comments' }
  },
  {
    path: '/lens/:lensId/spend-back/:section?',
    name: 'LensSpendBack',
    component: LensDashboardView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens', tab: 'spend-back', featureFlag: FEATURE_FLAGS.SPENDBACK }
  },
  {
    path: '/lens/:lensId/report/:reportId',
    name: 'LensReport',
    component: LensReportView,
    meta: { requiresAuth: true, theme: 'black', product: 'lens' }
  },
  {
    path: '/share/report/:reportId',
    name: 'LensReportPublicView',
    component: LensReportPublicView,
    meta: { theme: 'black', product: 'lens' }
  },
  {
    path: '/login',
    name: 'LoginView',
    component: LoginView,
    meta: { hideSidebar: true }
  },
  {
    path: '/mcp/oauth/authorize',
    name: 'McpOauthAuthorizeView',
    component: McpOauthAuthorizeView,
    meta: { hideSidebar: true }
  },
  {
    path: '/sign-up',
    name: 'SignUpView',
    component: SignUpView,
    meta: { hideSidebar: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPasswordView',
    component: ForgotPasswordView
  },
  {
    path: '/reset-password/:oobCode',
    name: 'ResetPasswordView',
    component: ResetPasswordView
  },
  {
    path: '/verify-email',
    name: 'VerifyEmailView',
    component: VerifyEmailView
  },
  {
    path: '/complete-verification/:oobCode',
    name: 'CompleteVerificationView',
    component: CompleteVerificationView
  },
  {
    path: '/email-sent',
    name: 'LoginEmailSentView',
    component: LoginEmailSentView
  },
  {
    path: '/auth-callback',
    name: 'AuthCallbackView',
    component: AuthCallbackView
  },
  {
    path: '/share/boards/:id',
    name: 'BoardsPublicView',
    component: BoardsPublicView,
    meta: { product: 'library' }
  },
  {
    path: '/share/ads/:id',
    name: 'AdsPublicView',
    component: AdsPublicView,
    meta: { product: 'library' }
  },
  {
    path: '/share/lens-creative/:id',
    name: 'AdsShareLensView',
    component: AdsShareLensView,
    meta: { hideSidebar: true, product: 'lens', theme: 'black' }
  },
  {
    path: '/share/lens-ad/:id',
    name: 'AdsShareLensView',
    component: AdsShareLensView,
    meta: { hideSidebar: true, product: 'lens', theme: 'black' }
  },
  {
    path: '/dashboard',
    name: 'DashboardView',
    component: DashboardView,
    meta: { requiresAuth: true, theme: 'blue' }
  },
  {
    path: '/launchpad',
    name: 'SetupView',
    component: SetupView,
    meta: { requiresAuth: true, theme: 'blue', product: 'setup' }
  },
  {
    path: '/briefs',
    name: 'BriefsView',
    component: BriefsView,
    meta: { requiresAuth: true, theme: 'green', product: 'briefs' }
  },
  {
    path: '/edit-briefs/:id',
    name: 'EditBriefView',
    component: EditBriefView,
    meta: { requiresAuth: true, theme: 'green', product: 'briefs' }
  },
  {
    path: '/share/briefs/:id',
    name: 'BriefPublicView',
    component: BriefPublicViewV2,
    meta: { theme: 'green', hideSidebar: true, product: 'briefs' }
  },
  {
    path: '/discovery',
    name: 'DiscoveryView',
    component: DiscoveryView,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery-brands',
    name: 'DiscoveryBrandsView',
    component: DiscoveryView,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery-experts',
    name: 'DiscoveryExpertsView',
    component: DiscoveryView,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery-experts/:id',
    name: 'DiscoveryExpertView',
    component: DiscoveryExpertView,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery/product-category/:category',
    name: 'DiscoveryProductCategoryView',
    component: DiscoveryAICategoryView,
    props: true,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery/creative-targeting/:category',
    name: 'DiscoveryCreativeTargetingView',
    component: DiscoveryAICategoryView,
    props: true,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/share/discovery-experts/:id',
    name: 'DiscoveryExpertPublicView',
    component: DiscoveryExpertPublicView,
    meta: { theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery-experts/:id/board/:boardId',
    name: 'DiscoveryExpertBoardPublicView',
    component: DiscoveryExpertView,
    meta: { theme: 'purple', product: 'discovery' }
  },
  {
    path: '/discovery-experts/:id/board/:boardId',
    name: 'DiscoveryExpertBoardView',
    component: DiscoveryExpertView,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/shared-discover/:id',
    name: 'SharedDiscover',
    component: AdsPublicViewDiscover,
    meta: { product: 'discovery' }
  },
  {
    path: '/library',
    name: 'LibraryView',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', liveUpdate: true, product: 'library' }
  },
  {
    // For tracking purposes
    path: '/welcome-49',
    name: 'Welcome49',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    // For tracking purposes
    path: '/welcome-99',
    name: 'Welcome99',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    path: '/library-brands',
    name: 'LibraryBrandsView',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    path: '/library-team',
    name: 'LibraryTeamView',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    path: '/library-mobile-saving',
    name: 'LibraryMobileSavingView',
    component: LibraryView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    path: '/spyder',
    name: 'SpyderBrandsView',
    component: SpyderBrandsView,
    meta: { requiresAuth: true, theme: 'red', product: 'spyder' }
  },
  {
    path: '/spyder/feed',
    name: 'SpyderFeedView',
    component: SpyderFeedView,
    meta: { requiresAuth: true, theme: 'red', product: 'spyder' }
  },
  {
    path: '/library-spyder/:id/:view?/:mode?',
    name: 'SpyderView',
    component: SpyderView,
    meta: { requiresAuth: true, theme: 'red', product: 'spyder' }
  },
  {
    path: '/boards',
    name: 'BoardsView',
    component: BoardsView,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  {
    path: '/boards/:id',
    name: 'BoardsDetailsView',
    component: BoardsDetailsView,
    props: true,
    meta: { requiresAuth: true, theme: 'blue', liveUpdate: true, product: 'library' }
  },
  {
    path: '/discovery/brands/:id',
    name: 'DiscoveryBrandView',
    component: DiscoveryBrandView,
    props: true,
    meta: { requiresAuth: true, theme: 'purple', product: 'discovery' }
  },
  {
    path: '/brands/:id',
    name: 'BrandView',
    component: BrandView,
    props: true,
    meta: { requiresAuth: true, theme: 'blue', product: 'library' }
  },
  // {
  //   path: '/account',
  //   name: 'AccountView',
  //   component: AccountView,
  //   meta: { requiresAuth: true, theme: 'blue' }
  // },
  {
    path: '/perks',
    name: 'PerksView',
    component: PerksView,
    meta: { requiresAuth: true, theme: 'blue' }
  },
  // {
  //   path: '/manage-subscription',
  //   name: 'PaymentView',
  //   component: PaymentViewV2,
  //   meta: { requiresAuth: true, hideSidebar: true }
  // },
  {
    path: '/thank-you-solo-monthly',
    name: 'MonthlySoloThankYouRedirectView',
    component: ThankYouRedirectView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/thank-you-solo-annual',
    name: 'AnnualSoloThankYouRedirectView',
    component: ThankYouRedirectView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/thank-you-team-monthly',
    name: 'MonthlyTeamThankYouRedirectView',
    component: ThankYouRedirectView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/thank-you-team-annual',
    name: 'AnnualTeamThankYouRedirectView',
    component: ThankYouRedirectView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/success-purchase',
    name: 'PurchaseSuccessView',
    component: SuccessPurchaseView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/getting-started',
    name: 'UserOnboardView',
    component: UserOnboardView,
    meta: { requiresAuth: true, hideSidebar: true }
  },
  {
    path: '/team-invite/:token',
    name: 'TeamInviteView',
    component: TeamInviteView,
    meta: { hideSidebar: true }
  },
  {
    path: '/auth-extension',
    name: 'ChromeExtensionAuthView',
    component: ChromeExtensionAuthView,
    meta: { requiresAuth: true }
  },
  {
    path: '/token-login/:token',
    name: 'TokenLoginView',
    component: TokenLoginView
  },
  {
    path: '/:utm',
    name: 'ExpertRedirect',
    component: ExpertRedirectView,
    meta: { hideSidebar: true }
  },
  {
    path: '*',
    redirect: { name: 'DiscoveryView' }
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

// Limited route names - compute to.meta.product and check against corresponding limits
const limitedRoutes = [
  'SpyderView',
  'LensDashboardView',
  'LensReport',
  'Lens'
]

const guestBlockedRoutes = [
  'spyder',
  'briefs',
  'library',
  'discovery'
]

// Lazy route chunks are content-hashed, so a deploy renames them. A tab opened
// before the deploy still requests the old filename; the host's SPA fallback
// answers with index.html (200), webpack rejects with "Loading chunk failed",
// and the navigation dies silently — the click looks dead. Reload once onto the
// target route so the tab picks up the new build; sessionStorage guards
// against looping if the chunk is genuinely broken.
const CHUNK_RELOAD_KEY = 'fp_chunk_reload_target'
let pendingNavigationPath = null

const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading (CSS )?chunk \S+ failed/i.test(error?.message || '')

const readChunkReloadTarget = () => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY)
  } catch (storageError) {
    console.error('Could not read chunk reload guard', storageError)
    return null
  }
}

// Returns true only when the value was actually persisted. Storage can be
// blocked or zero-quota (private browsing), and reloading without a working
// guard would loop forever.
const writeChunkReloadTarget = (value) => {
  try {
    if (value === null) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY)
      return sessionStorage.getItem(CHUNK_RELOAD_KEY) === null
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, value)
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) === value
  } catch (storageError) {
    console.error('Could not write chunk reload guard', storageError)
    return false
  }
}

router.onError((error) => {
  if (!isChunkLoadError(error)) return
  const target = pendingNavigationPath || window.location.pathname + window.location.search
  const alreadyReloaded = readChunkReloadTarget() === target
  if (!alreadyReloaded && writeChunkReloadTarget(target)) {
    window.location.assign(target)
    return
  }
  // Either this route already failed once after a reload, or the guard could
  // not be persisted (blocked storage). Never reload without a working guard.
  console.error('Route chunk failed to load; not reloading automatically', error)
  EventBus.$emit('showAlert', {
    type: 'error',
    message: 'A new version of Foreplay is available. Please refresh the page to continue.'
  })
})

// Route Guard
router.beforeEach(async (to, from, next) => {
  pendingNavigationPath = to.fullPath

  // Capture gclid/fbclid here, before any auth redirect below moves them into a nested redirect query param
  if (to.query.gclid || to.query.fbclid) {
    const adClickIds = {
      gclid: to.query.gclid || null,
      fbclid: to.query.fbclid || null
    }
    posthog.capture('Paid Landing Page Viewed', {
      $current_url: window.location.origin + to.fullPath,
      ...adClickIds
    })

    // No person profile exists yet at this point, so save the click id and attach it on login instead
    try {
      localStorage.setItem('fp_ad_click_ids', JSON.stringify(adClickIds))
    } catch (error) {
      console.log('Error persisting ad click ids', error)
    }
  }

  const isAuthenticated = store.getters['AuthModule/isUserLoggedIn']
  const isFreeTier = store.getters['AuthModule/isFreeTier']
  const team = store.getters['AuthModule/getTeam']
  const userFreeTrialDate = store.getters['AuthModule/getUserFreeTrialDate']
  const isInspoMode = store.getters['BriefsModule/getIsInspoMode']
  const activeLensId = store.getters['LensModule/getActiveLensId']
  const spyderLimit = store.getters['AuthModule/getSpyderLimit']
  const lensLimit = store.getters['AuthModule/getTotalLensLimit']
  const initialLoad = store.getters['AuthModule/getInitialLoad']
  const guestStatus = store.getters['AuthModule/getGuestStatus']
  const isLegacy = store.getters['AuthModule/getIsLegacy']
  const hasApiAccess = store.getters['AuthModule/getHasApiAccess']
  const subscriptionState = store.getters['AuthModule/getUserSubscription']?.status
  const isPastDue = subscriptionState === SUBSCRIPTION_STATES.PAST_DUE
  const currentUser = firebase.auth().currentUser
  const isEmailVerified = currentUser ? currentUser.emailVerified : false

  const needsUpgrade = isFreeTier && Date.now() > userFreeTrialDate + 6.048e8

  store.commit('RouterModule/SET_LAST_ROUTE', from.name)
  store.commit('RouterModule/SET_CURRENT_ROUTE', to.name)
  store.commit('RouterModule/SET_CURRENT_PARAMS', to.params)
  store.commit('RouterModule/SET_CURRENT_ROUTE_PRODUCT', to.meta.product ?? null)
  store.commit('AdvertisementsModule/SET_NEW_ADS', [])

  await clearUniqueCreatives()
  await clearUniqueHooks()

  if (to.meta.betaLocked) {
    if (!store.getters['AuthModule/isBetaUser']) {
      next({ name: 'DiscoveryView' })
      return
    }
  }

  // Don't block on flags loading — only redirect once they're known to be off; views react when they arrive
  if (to.meta.featureFlag && store.getters['MiscModule/getFeatureFlagsLoaded'] &&
    !store.getters['MiscModule/isFeatureEnabled'](to.meta.featureFlag)) {
    next({ name: 'LensDashboardView', params: to.params })
    return
  }

  // Check the theme
  if (to.meta.theme) {
    if (isInspoMode) {
      store.commit('MiscModule/SET_THEME', 'green')
    } else {
      store.commit('MiscModule/SET_THEME', to.meta.theme)
    }
  }

  // Check if navigating to a different Lens, if so, set activeLensId and refetch metrics/presets
  // The demo Lens (Launchpad demo reports) is served locally: it joins the
  // Lens lists while open and loads metrics from the demo registry
  const isDemoRoute = isDemoLensRoute(to.params)
  if (to.meta.product === 'lens' && to.params?.lensId !== activeLensId) {
    const lensId = to.params?.lensId ?? null
    store.commit('LensModule/SET_ACTIVE_LENS_ID', lensId)
    if (isDemoLensId(lensId)) {
      store.dispatch('LensModule/enterDemoLens')
      store.dispatch('MetricsModule/fetchLensMetrics', { lensId, registryOverride: DEMO_METRICS_REGISTRY })
    } else {
      if (isDemoLensId(from.params?.lensId)) store.dispatch('LensModule/exitDemoLens')
      store.dispatch('MetricsModule/fetchLensMetrics', { lensId })
    }
    store.dispatch('MetricsModule/fetchPresets', lensId)
    store.dispatch('LensModule/fetchTags', lensId)
  } else if (to.meta.product === 'lens') {
    if (isDemoRoute && !store.getters['LensModule/getIsDemoLensActive']) {
      store.dispatch('LensModule/enterDemoLens')
    }
    // Otherwise, we're navigating to a general lens page without having metrics/presets yet, so fetch them
    if (_.isEmpty(store.getters['MetricsModule/getMetrics'])) {
      store.dispatch('MetricsModule/fetchLensMetrics', isDemoRoute
        ? { lensId: to.params.lensId, registryOverride: DEMO_METRICS_REGISTRY }
        : undefined)
    }
    if (_.isEmpty(store.getters['MetricsModule/getPresets'])) {
      store.dispatch('MetricsModule/fetchPresets', isDemoRoute ? to.params.lensId : undefined)
    }
  } else if (isDemoLensId(from.params?.lensId)) {
    store.dispatch('LensModule/exitDemoLens')
  }

  // store queries in cookies
  if (to.query.utm_source) {
    store.commit('MiscModule/SET_UTM_SOURCE', to.query.utm_source)
  }
  if (to.query.utm_medium) {
    store.commit('MiscModule/SET_UTM_MEDIUM', to.query.utm_medium)
  }
  if (to.query.utm_campaign) {
    store.commit('MiscModule/SET_UTM_CAMPAIGN', to.query.utm_campaign)
  }

  // Check if the user is logged in
  if (to.name !== 'LoginView' && !isAuthenticated && to.meta.requiresAuth) {
    next({ name: 'LoginView', query: { redirect: to.fullPath } })
    return
  }

  // Check if the user is email verified
  if (
    !['VerifyEmailView', 'CompleteVerificationView', 'LoginView', 'SignUpView', 'TeamInviteView'].includes(to.name) &&
    !isEmailVerified &&
    isAuthenticated &&
    to.meta.requiresAuth
  ) {
    if (from.query.authOob && from.name === 'LoginView') {
      next({ name: 'CompleteVerificationView', params: { oobCode: from.query.authOob } })
    } else {
      next({ name: 'VerifyEmailView' })
    }
    return
  }

  // Check if the user is onboarded
  const onboardingStatus = store.getters['AuthModule/getUserOnboardingStatus']
  const onboardingCompleted = onboardingStatus === 'FINISH'
  if (
    !['LoginView', 'SignUpView', 'UserOnboardView', 'VerifyEmailView', 'CompleteVerificationView', 'TeamInviteView'].includes(to.name) &&
    !onboardingCompleted &&
    isAuthenticated &&
    to.meta.requiresAuth
  ) {
    const prevQueryParams = to?.query || {}
    next({ name: 'UserOnboardView', query: prevQueryParams })
    return
  }

  if (limitedRoutes.includes(to.name) && !isDemoRoute) {
    const limit = to.meta.product === 'spyder' ? spyderLimit : to.meta.product === 'lens' ? (lensLimit || (guestStatus?.isGuest || guestStatus?.isGuestOnly)) : null
    if (limit !== null && limit === 0) {
      const paywallRedirect = to.meta.product === 'spyder' ? 'SpyderBrandsView' : 'LensView'
      next({ name: paywallRedirect })
      return
    }
  }

  // Setup checklist is not available to guest-only users
  if (to.meta?.product === 'setup' && guestStatus?.isGuestOnly) {
    next({ name: 'LensView' })
    return
  }

  // Setup checklist is only for trialing users
  if (to.meta?.product === 'setup' && !isFreeTier) {
    next({ name: 'DiscoveryView' })
    return
  }

  // Check if guest is trying to access a paywall route
  const blockedRoutes = [
    ...guestBlockedRoutes,
    ...(!guestStatus?.isGuestOnly ? ['lens'] : []), // Don't include lens as a blocked route for guestOnly users
    ...(isLegacy && !hasApiAccess ? ['api'] : [])
  ]
  if (
    (guestStatus?.isGuestOnly || (!subscriptionState || subscriptionState === SUBSCRIPTION_STATES.NOT_ACTIVE)) &&
    blockedRoutes.includes(to.meta?.product) &&
    to.meta?.requiresAuth &&
    initialLoad &&
    !isDemoRoute
  ) {
    console.log('Guest user trying to access paywall route', to.meta?.product)
    store.commit('MiscModule/SET_SUBSCRIPTION_DRAWER_VIEW_MODE', { state: true })
    if (guestStatus?.isGuestOnly) {
      next({ name: 'LensView' })
    } else {
      next({ name: 'DashboardView' })
    }
    return
  }

  // Api redirect on past due payments
  const apiPaymentPastDue = store.getters['AuthModule/getApiSubscription']?.status === SUBSCRIPTION_STATES.PAST_DUE
  if (apiPaymentPastDue && to.meta?.product === 'api') {
    EventBus.$emit('showAlert', { type: 'error', message: 'Your API payment is past due. Please update your payment method to continue using the API.' })
    next({ name: 'DiscoveryView' })
    return
  }

  // Product paywall on past due payments
  if (isPastDue && ['lens', 'spyder', 'briefs', 'library'].includes(to.meta?.product) && !isDemoRoute) {
    store.commit('MiscModule/SET_THEME', 'purple')
    next({ name: 'DiscoveryView' })
    return
  }

  if (['LoginView', 'LoginEmailSentView'].includes(to.name) && isAuthenticated) {
    if (isAppRedirect(to.query.redirect)) {
      next(to.query.redirect)
    } else {
      next({ name: 'DashboardView' })
    }
    return
  }

  next()
})

// Track page views with PostHog
router.afterEach((to, from) => {
  // Navigation succeeded, so any chunk-reload guard from a previous attempt is stale
  if (readChunkReloadTarget() !== null) writeChunkReloadTarget(null)
  posthog.capture('$pageview', {
    $current_url: window.location.href
  })
})

export default router
